import { z } from 'zod'
import { CONTENT_VERSION } from '@/services/contentCatalog.js'
import {
  createContentReferenceIndex,
  reconcileContentVersion,
  validateContentReferences,
  type ContentReferenceIndex,
} from '@/services/content-migration/contentMigration.js'
import {
  DailyStatisticSchema,
  LearningProgressSchema,
  UserDatabaseSchema,
  UserDatabaseV1Schema,
  UserDataExportSchema,
  UserDataExportV1Schema,
  WrongQuestionRecordSchema,
  type UserDatabase,
  type UserDatabaseV1,
  type UserSettings,
} from '@/types/user-data.js'
import { createLocalForageAdapter, type StorageAdapter } from './storageAdapter.js'

export const USER_DATABASE_KEY = 'user-database'
export const CURRENT_USER_SCHEMA_VERSION = 2

const DEFAULT_SETTINGS: UserSettings = {
  dailyNewKnowledge: 10,
  maxDailyReviews: 50,
  defaultCategory: null,
  randomOrder: false,
  showMastered: true,
  themeMode: 'system',
}

const UserSettingsSchemaPartial = z
  .object({
    dailyNewKnowledge: z.number().int().min(1).max(100).optional(),
    maxDailyReviews: z.number().int().min(1).max(500).optional(),
    defaultCategory: z.string().nullable().optional(),
    randomOrder: z.boolean().optional(),
    showMastered: z.boolean().optional(),
    themeMode: z.enum(['light', 'dark', 'system']).optional(),
  })
  .optional()

const LegacyDatabaseSchema = z
  .object({
    schemaVersion: z.literal(0).optional(),
    progress: z.record(z.unknown()).optional(),
    wrongQuestions: z.record(z.unknown()).optional(),
    settings: UserSettingsSchemaPartial,
    dailyStatistics: z.array(z.unknown()).optional(),
    recentKnowledgeId: z.string().optional(),
  })
  .passthrough()

function now(): string {
  return new Date().toISOString()
}

export class UserDataError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options)
    this.name = 'UserDataError'
  }
}

export class UnsupportedSchemaVersionError extends UserDataError {
  constructor(version: number) {
    super(`学习数据版本 ${version} 高于当前支持的版本 ${CURRENT_USER_SCHEMA_VERSION}。`)
    this.name = 'UnsupportedSchemaVersionError'
  }
}

function createDefaultDatabase(contentVersion = CONTENT_VERSION): UserDatabase {
  return {
    schemaVersion: CURRENT_USER_SCHEMA_VERSION,
    contentVersion,
    updatedAt: now(),
    progress: {},
    wrongQuestions: {},
    questionAttempts: {},
    practiceSessions: {},
    settings: { ...DEFAULT_SETTINGS },
    dailyStatistics: [],
  }
}

function migrateV1(database: UserDatabaseV1): UserDatabase {
  return UserDatabaseSchema.parse({
    ...database,
    schemaVersion: CURRENT_USER_SCHEMA_VERSION,
    questionAttempts: {},
    practiceSessions: {},
  })
}

function schemaVersionOf(raw: unknown): number {
  if (!raw || typeof raw !== 'object') return 0
  const value = (raw as Record<string, unknown>).schemaVersion
  return typeof value === 'number' ? value : 0
}

function migrateLegacy(raw: unknown, contentVersion: string): UserDatabase {
  const parsed = LegacyDatabaseSchema.safeParse(raw)
  if (!parsed.success) return createDefaultDatabase(contentVersion)
  const legacySettings = parsed.data.settings
  const progress: UserDatabase['progress'] = {}
  for (const [id, value] of Object.entries(parsed.data.progress ?? {})) {
    const record = LearningProgressSchema.safeParse(value)
    if (record.success) progress[id] = record.data
  }
  const wrongQuestions: UserDatabase['wrongQuestions'] = {}
  for (const [id, value] of Object.entries(parsed.data.wrongQuestions ?? {})) {
    const record = WrongQuestionRecordSchema.safeParse(value)
    if (record.success) wrongQuestions[id] = record.data
  }
  const dailyStatistics: UserDatabase['dailyStatistics'] = []
  for (const value of parsed.data.dailyStatistics ?? []) {
    const statistic = DailyStatisticSchema.safeParse(value)
    if (statistic.success) dailyStatistics.push(statistic.data)
  }
  return {
    ...createDefaultDatabase(contentVersion),
    progress,
    wrongQuestions,
    dailyStatistics,
    ...(parsed.data.recentKnowledgeId ? { recentKnowledgeId: parsed.data.recentKnowledgeId } : {}),
    settings: {
      dailyNewKnowledge: legacySettings?.dailyNewKnowledge ?? DEFAULT_SETTINGS.dailyNewKnowledge,
      maxDailyReviews: legacySettings?.maxDailyReviews ?? DEFAULT_SETTINGS.maxDailyReviews,
      defaultCategory: legacySettings?.defaultCategory ?? DEFAULT_SETTINGS.defaultCategory,
      randomOrder: legacySettings?.randomOrder ?? DEFAULT_SETTINGS.randomOrder,
      showMastered: legacySettings?.showMastered ?? DEFAULT_SETTINGS.showMastered,
      themeMode: legacySettings?.themeMode ?? DEFAULT_SETTINGS.themeMode,
    },
  }
}

function parseAndMigrateDatabase(raw: unknown, contentVersion: string): UserDatabase {
  const version = schemaVersionOf(raw)
  if (version > CURRENT_USER_SCHEMA_VERSION) throw new UnsupportedSchemaVersionError(version)
  if (version === 0) return migrateLegacy(raw, contentVersion)
  if (version === 1) return migrateV1(UserDatabaseV1Schema.parse(raw))
  return UserDatabaseSchema.parse(raw)
}

function sameAttempt(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right)
}

export class UserDataRepository {
  private readonly referenceIndex: ContentReferenceIndex

  constructor(
    private readonly storage: StorageAdapter = createLocalForageAdapter(),
    private readonly contentVersion = CONTENT_VERSION,
    referenceIndex: ContentReferenceIndex = createContentReferenceIndex(),
  ) {
    this.referenceIndex = referenceIndex
  }

  async load(): Promise<UserDatabase> {
    const raw = await this.storage.getItem<unknown>(USER_DATABASE_KEY)
    if (raw === null) {
      const database = createDefaultDatabase(this.contentVersion)
      await this.storage.setItem(USER_DATABASE_KEY, database)
      return database
    }

    const version = schemaVersionOf(raw)
    const database = parseAndMigrateDatabase(raw, this.contentVersion)
    const reconciled = reconcileContentVersion(database, this.contentVersion, this.referenceIndex)
    if (version < CURRENT_USER_SCHEMA_VERSION || reconciled !== database) {
      await this.storage.setItem(USER_DATABASE_KEY, reconciled)
    }
    return reconciled
  }

  async save(database: UserDatabase): Promise<UserDatabase> {
    const validated = UserDatabaseSchema.parse({ ...database, updatedAt: now() })
    const stored = await this.storage.getItem<unknown>(USER_DATABASE_KEY)
    if (stored !== null && schemaVersionOf(stored) === CURRENT_USER_SCHEMA_VERSION) {
      const current = UserDatabaseSchema.parse(stored)
      for (const [attemptId, attempt] of Object.entries(current.questionAttempts)) {
        if (!sameAttempt(attempt, validated.questionAttempts[attemptId])) {
          throw new UserDataError(`答题记录 ${attemptId} 是不可覆盖历史，不能修改或删除。`)
        }
      }
    }
    await this.storage.setItem(USER_DATABASE_KEY, validated)
    return validated
  }

  async export(): Promise<string> {
    const database = await this.load()
    return JSON.stringify(
      {
        schemaVersion: CURRENT_USER_SCHEMA_VERSION,
        exportedAt: now(),
        data: database,
      },
      null,
      2,
    )
  }

  async import(serialized: string): Promise<UserDatabase> {
    let raw: unknown
    try {
      raw = JSON.parse(serialized) as unknown
    } catch (error) {
      throw new UserDataError('导入文件不是有效的 JSON。', { cause: error })
    }

    const version = schemaVersionOf(raw)
    if (version > CURRENT_USER_SCHEMA_VERSION) throw new UnsupportedSchemaVersionError(version)
    const importedDataVersion =
      raw && typeof raw === 'object' && 'data' in raw
        ? schemaVersionOf((raw as { data?: unknown }).data)
        : 0
    if (importedDataVersion > CURRENT_USER_SCHEMA_VERSION) {
      throw new UnsupportedSchemaVersionError(importedDataVersion)
    }
    const parsedV2 = UserDataExportSchema.safeParse(raw)
    const parsedV1 = UserDataExportV1Schema.safeParse(raw)
    if (!parsedV2.success && !parsedV1.success) {
      throw new UserDataError(
        `导入数据格式不正确：${parsedV2.error.issues[0]?.message ?? '未知错误'}`,
      )
    }
    let imported: UserDatabase
    if (parsedV2.success) {
      imported = parsedV2.data.data
    } else if (parsedV1.success) {
      imported = migrateV1(parsedV1.data.data)
    } else {
      throw new UserDataError('导入数据格式不正确。')
    }
    const allowOrphans = imported.contentVersion !== this.contentVersion
    const referenceIssues = validateContentReferences(imported, this.referenceIndex, {
      // Test and migration repositories may intentionally use a synthetic content version.
      // The production repository uses CONTENT_VERSION and performs strict current-content checks.
      allowOrphans: allowOrphans || this.contentVersion !== CONTENT_VERSION,
    })
    if (referenceIssues.length) {
      throw new UserDataError(
        `导入数据引用不完整：${referenceIssues[0]?.path} ${referenceIssues[0]?.message}`,
      )
    }
    const reconciled = reconcileContentVersion(imported, this.contentVersion, this.referenceIndex)
    await this.storage.setItem(USER_DATABASE_KEY, reconciled)
    return reconciled
  }

  async clear(): Promise<void> {
    await this.storage.removeItem(USER_DATABASE_KEY)
  }
}
